import { preventInteractions } from "@hoylu/client-common";
import type { NexusStatusEntity } from "@hoylu/nexus-service";
import { decode } from "html-entities";
import React, { useRef } from "react";
import { useDispatch } from "react-redux";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { activateDashboardOption } from "../../../state/workspaces/workspaces.actions";
import { useI18n } from "../../../utils/hooks/use.i18n";
import Styles from "./TaskView.module.css";

type TitleCellProps = {
  title: string;
};

export const TitleCell = ({ title }: TitleCellProps) => {
  const classNames = [Styles.textLeft];
  return (
    <div className={classNames.join(" ")} title={title}>
      {decode(title)}
    </div>
  );
};

export type WeekCellProps = {
  weekNumber: string;
};

export function WeekCell({ weekNumber }: WeekCellProps) {
  return <div title={weekNumber}>{weekNumber}</div>;
}

export type StatusCellProps = {
  status: NexusStatusEntity;
  reason?: string;
};

export function StatusCell({
  status,
  reason,
}: StatusCellProps): React.JSX.Element {
  //There is extreamly rare edge case where the status is undefined.
  //In this case we will show a question mark icon.
  //This is to prevent the table from crashing and indicates user he need to reload master data.
  return (
    <div className={Styles.statusContainer}>
      <div
        className={`${Styles.icon} ${
          status?.icon || "hoylu-ui-icons-question-mark"
        }`}
        title={reason ? reason : status?.name}
      />
    </div>
  );
}

type LocationCellProps = {
  taskId: string;
  workspaces: string[];
};

/** Supports pdf hyperlinks to workspace elements */
export function LocationCell({
  taskId,
  workspaces,
}: LocationCellProps): React.JSX.Element {
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

  const t = useI18n("ANALYTICS.");
  const dispatch = useDispatch();

  const getPositionPoint = () => {
    const bounds = ref.current?.getBoundingClientRect();

    if (bounds) {
      const leftShift = 165; // around half of modal width plus small margin

      return {
        x: bounds.x - leftShift,
        y: bounds.y,
      };
    }

    return undefined;
  };

  const handleCellClick = () => {
    dispatch(
      activateDashboardOption({
        optionType: DashboardOption.SHOW_TASK_LOCATIONS,
        taskLocationsData: { taskId, workspaces },
        dialogPosition: getPositionPoint(),
      }),
    );
  };

  return (
    <div
      className={[Styles.linkContainer, Styles.clickable].join(" ")}
      {...preventInteractions()}
      onClick={handleCellClick}
      ref={ref}
      title={t("LOCATIONS_TOOLTIP")}
    >
      <div className={[Styles.icon, "hoylu-ui-icons-blank"].join(" ")} />
      <span>{`(${workspaces.length})`}</span>
    </div>
  );
}
